import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TagModule } from 'primeng/tag';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService } from 'primeng/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { CategoryService } from '../../core/services/category.service';
import { UnitOfMeasureService } from '../../core/services/unit-of-measure.service';
import { StoreService } from '../../core/services/store.service';
import { StoreTypesService } from '../../core/services/store-types.service';
import { VariantAttributeService } from '../../core/services/variant-attribute.service';
import { CategoryDto, UnitOfMeasureDto } from '../../core/models/product.models';
import { VariantAttributeDto } from '../../core/models/variant.models';
import { StoreLiteDto, StoreTypeDto } from '../../core/models/store.models';
import { storePicker } from '../../core/utils/store-picker';

@Component({
  selector: 'app-categories',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    ToggleSwitchModule,
    TagModule,
    ConfirmDialogModule,
    TooltipModule,
    TranslatePipe,
  ],
  providers: [ConfirmationService],
  templateUrl: './categories.component.html',
})
export class CategoriesComponent implements OnInit {
  private categorySvc = inject(CategoryService);
  private unitSvc = inject(UnitOfMeasureService);
  private storeSvc = inject(StoreService);
  private storeTypeSvc = inject(StoreTypesService);
  private attrSvc = inject(VariantAttributeService);
  private confirmSvc = inject(ConfirmationService);
  private translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  activeTab: 'categories' | 'units' | 'attributes' = 'categories';

  // ── Stores (storeType uchun) ──────────────────────────────────────────────────
  stores = signal<StoreLiteDto[]>([]);

  /** Bitta do'konli biznesda tanlagichlar yashiriladi — `storePicker` ga qarang. */
  readonly storePick = storePicker(this.stores);
  storeTypes = signal<StoreTypeDto[]>([]);

  // ── Umumiy do'kon turi filteri (barcha tablarda ishlaydi) ─────────────────────
  storeTypeFilter: string | null = null;

  // ── Categories ────────────────────────────────────────────────────────────────
  categories = signal<CategoryDto[]>([]);
  catLoading = signal(false);
  catSearch = '';

  catDialogVisible = false;
  catSaving = false;
  catEditingId: string | null = null;
  catForm: { name: string; isActive: boolean; storeId: string | null } = {
    name: '',
    isActive: true,
    storeId: null,
  };

  get isCatEdit(): boolean {
    return !!this.catEditingId;
  }

  // ── Units ─────────────────────────────────────────────────────────────────────
  units = signal<UnitOfMeasureDto[]>([]);
  unitLoading = signal(false);
  unitSearch = '';

  unitDialogVisible = false;
  unitSaving = false;
  unitEditingId: string | null = null;
  unitForm: { name: string; isActive: boolean; storeId: string | null } = {
    name: '',
    isActive: true,
    storeId: null,
  };

  get isUnitEdit(): boolean {
    return !!this.unitEditingId;
  }

  // ── Variant atributlari ─────────────────────────────────────────────────────────
  attributes = signal<VariantAttributeDto[]>([]);
  attrLoading = signal(false);
  attrSearch = '';

  attrDialogVisible = false;
  attrSaving = false;
  attrEditingId: string | null = null;
  attrForm: {
    name: string;
    storeId: string | null;
    isActive: boolean;
    values: { id: string | null; value: string }[];
  } = {
    name: '',
    storeId: null,
    isActive: true,
    values: [],
  };

  get isAttrEdit(): boolean {
    return !!this.attrEditingId;
  }

  ngOnInit() {
    this.storeSvc
      .getAllLite()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (res) => this.stores.set(res.data ?? []) });
    this.storeTypeSvc
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (res) => this.storeTypes.set(res.data ?? []) });
    this.loadCategories();
  }

  switchTab(tab: 'categories' | 'units' | 'attributes') {
    this.activeTab = tab;
    if (tab === 'categories') this.loadCategories();
    else if (tab === 'units') this.loadUnits();
    else this.loadAttributes();
  }

  // ── Category methods ──────────────────────────────────────────────────────────

  loadCategories() {
    this.catLoading.set(true);
    this.categorySvc
      .getAll(this.catSearch || undefined, false, this.storeTypeFilter)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.categories.set(res.data ?? []),
        error: (err) => {
          this.catLoading.set(false);
          this.categorySvc.showLoadError(err);
        },
        complete: () => this.catLoading.set(false),
      });
  }

  onCatSearch() {
    this.loadCategories();
  }

  onStoreTypeFilter() {
    if (this.activeTab === 'categories') this.loadCategories();
    else if (this.activeTab === 'units') this.loadUnits();
    else this.loadAttributes();
  }

  openCreateCat() {
    this.catEditingId = null;
    // Do'kon bitta bo'lsa tanlagich ko'rsatilmaydi — turi shu qiymatdan olinadi.
    this.catForm = { name: '', isActive: true, storeId: this.storePick.single()?.id ?? null };
    this.catDialogVisible = true;
  }

  openEditCat(cat: CategoryDto) {
    this.catEditingId = cat.id;
    this.catForm = { name: cat.name, isActive: cat.isActive, storeId: null };
    this.catDialogVisible = true;
  }

  selectedStoreTypeId(): string | null {
    if (!this.catForm.storeId) return null;
    return this.stores().find((s) => s.id === this.catForm.storeId)?.storeTypeId ?? null;
  }

  selectedStoreTypeName(): string | null {
    if (!this.catForm.storeId) return null;
    return this.stores().find((s) => s.id === this.catForm.storeId)?.storeTypeName ?? null;
  }

  closeCatDialog() {
    this.catDialogVisible = false;
  }

  saveCat() {
    if (!this.catForm.name.trim()) {
      this.categorySvc.showWarn(this.translate.instant('CATEGORIES.TOAST.NAME_REQUIRED'));
      return;
    }
    // Yaratishda do'kon (storeType) majburiy — kategoriya shu turga bog'lanadi.
    // Tanlagich yashirilgan bo'lsa qiymat forma ochilishida qo'yiladi; do'kon
    // ro'yxati kechikib kelgan holat uchun zaxira.
    this.catForm.storeId ??= this.storePick.single()?.id ?? null;
    if (!this.catEditingId && !this.catForm.storeId) {
      this.categorySvc.showWarn(this.translate.instant('CATEGORIES.TOAST.STORE_REQUIRED'));
      return;
    }
    this.catSaving = true;
    const obs = this.catEditingId
      ? this.categorySvc.update(this.catEditingId, this.catForm.name.trim(), this.catForm.isActive)
      : this.categorySvc.create(this.catForm.name.trim(), this.selectedStoreTypeId());
    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        if (this.catEditingId) this.categorySvc.showUpdateSuccess();
        else this.categorySvc.showCreateSuccess();
        this.closeCatDialog();
        this.loadCategories();
      },
      error: (err) => {
        this.catSaving = false;
        if (this.catEditingId) this.categorySvc.showUpdateError(err);
        else this.categorySvc.showCreateError(err);
      },
      complete: () => (this.catSaving = false),
    });
  }

  confirmDeleteCat(cat: CategoryDto) {
    this.confirmSvc.confirm({
      message: this.translate.instant('CATEGORIES.DELETE_CONFIRM', { name: cat.name }),
      header: this.translate.instant('CATEGORIES.DELETE_TITLE'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('COMMON.DELETE'),
      rejectLabel: this.translate.instant('COMMON.CANCEL'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => {
        this.categorySvc
          .delete(cat.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.categorySvc.showDeleteSuccess();
              this.loadCategories();
            },
            error: (err) => this.categorySvc.showDeleteError(err),
          });
      },
    });
  }

  // ── Unit methods ──────────────────────────────────────────────────────────────

  loadUnits() {
    this.unitLoading.set(true);
    this.unitSvc
      .getAll(this.unitSearch || undefined, this.storeTypeFilter)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.units.set(res.data ?? []),
        error: (err) => {
          this.unitLoading.set(false);
          this.unitSvc.showLoadError(err);
        },
        complete: () => this.unitLoading.set(false),
      });
  }

  onUnitSearch() {
    this.loadUnits();
  }

  openCreateUnit() {
    this.unitEditingId = null;
    // Do'kon bitta bo'lsa tanlagich ko'rsatilmaydi — turi shu qiymatdan olinadi.
    this.unitForm = { name: '', isActive: true, storeId: this.storePick.single()?.id ?? null };
    this.unitDialogVisible = true;
  }

  openEditUnit(unit: UnitOfMeasureDto) {
    this.unitEditingId = unit.id;
    this.unitForm = { name: unit.name, isActive: unit.isActive, storeId: null };
    this.unitDialogVisible = true;
  }

  selectedUnitStoreTypeId(): string | null {
    if (!this.unitForm.storeId) return null;
    return this.stores().find((s) => s.id === this.unitForm.storeId)?.storeTypeId ?? null;
  }

  selectedUnitStoreTypeName(): string | null {
    if (!this.unitForm.storeId) return null;
    return this.stores().find((s) => s.id === this.unitForm.storeId)?.storeTypeName ?? null;
  }

  closeUnitDialog() {
    this.unitDialogVisible = false;
  }

  saveUnit() {
    if (!this.unitForm.name.trim()) {
      this.unitSvc.showWarn(this.translate.instant('UNITS.TOAST.NAME_REQUIRED'));
      return;
    }
    // Yaratishda do'kon (storeType) majburiy — birlik shu turga bog'lanadi.
    // Tanlagich yashirilgan bo'lsa qiymat forma ochilishida qo'yiladi; do'kon
    // ro'yxati kechikib kelgan holat uchun zaxira.
    this.unitForm.storeId ??= this.storePick.single()?.id ?? null;
    if (!this.unitEditingId && !this.unitForm.storeId) {
      this.unitSvc.showWarn(this.translate.instant('UNITS.TOAST.STORE_REQUIRED'));
      return;
    }
    this.unitSaving = true;
    const obs = this.unitEditingId
      ? this.unitSvc.update(this.unitEditingId, this.unitForm.name.trim(), this.unitForm.isActive)
      : this.unitSvc.create(this.unitForm.name.trim(), this.selectedUnitStoreTypeId());
    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        if (this.unitEditingId) this.unitSvc.showUpdateSuccess();
        else this.unitSvc.showCreateSuccess();
        this.closeUnitDialog();
        this.loadUnits();
      },
      error: (err) => {
        this.unitSaving = false;
        if (this.unitEditingId) this.unitSvc.showUpdateError(err);
        else this.unitSvc.showCreateError(err);
      },
      complete: () => (this.unitSaving = false),
    });
  }

  confirmDeleteUnit(unit: UnitOfMeasureDto) {
    this.confirmSvc.confirm({
      message: this.translate.instant('UNITS.DELETE_CONFIRM', { name: unit.name }),
      header: this.translate.instant('UNITS.DELETE_TITLE'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('COMMON.DELETE'),
      rejectLabel: this.translate.instant('COMMON.CANCEL'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => {
        this.unitSvc
          .delete(unit.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.unitSvc.showDeleteSuccess();
              this.loadUnits();
            },
            error: (err) => this.unitSvc.showDeleteError(err),
          });
      },
    });
  }

  // ── Variant attribute methods ───────────────────────────────────────────────────

  loadAttributes() {
    this.attrLoading.set(true);
    this.attrSvc
      .getAll(this.attrSearch || undefined, true, this.storeTypeFilter)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.attributes.set(res.data ?? []),
        error: (err) => {
          this.attrLoading.set(false);
          this.attrSvc.showLoadError(err);
        },
        complete: () => this.attrLoading.set(false),
      });
  }

  onAttrSearch() {
    this.loadAttributes();
  }

  selectedAttrStoreTypeId(): string | null {
    if (!this.attrForm.storeId) return null;
    return this.stores().find((s) => s.id === this.attrForm.storeId)?.storeTypeId ?? null;
  }

  selectedAttrStoreTypeName(): string | null {
    if (!this.attrForm.storeId) return null;
    return this.stores().find((s) => s.id === this.attrForm.storeId)?.storeTypeName ?? null;
  }

  openCreateAttr() {
    this.attrEditingId = null;
    // Do'kon bitta bo'lsa tanlagich ko'rsatilmaydi — turi shu qiymatdan olinadi.
    this.attrForm = {
      name: '',
      storeId: this.storePick.single()?.id ?? null,
      isActive: true,
      values: [{ id: null, value: '' }],
    };
    this.attrDialogVisible = true;
  }

  openEditAttr(attr: VariantAttributeDto) {
    this.attrEditingId = attr.id;
    const values = attr.values.filter((v) => v.isActive).map((v) => ({ id: v.id, value: v.value }));
    this.attrForm = {
      name: attr.name,
      storeId: null,
      isActive: attr.isActive,
      values: values.length ? values : [{ id: null, value: '' }],
    };
    this.attrDialogVisible = true;
  }

  addAttrValueRow() {
    this.attrForm.values.push({ id: null, value: '' });
  }

  removeAttrValueRow(index: number) {
    this.attrForm.values.splice(index, 1);
  }

  closeAttrDialog() {
    this.attrDialogVisible = false;
  }

  saveAttr() {
    // Tanlagich yashirilgan bo'lsa qiymat forma ochilishida qo'yiladi; do'kon
    // ro'yxati kechikib kelgan holat uchun zaxira.
    this.attrForm.storeId ??= this.storePick.single()?.id ?? null;

    const name = this.attrForm.name.trim();
    if (!name) {
      this.attrSvc.showWarn(this.translate.instant('VARIANT_ATTRS.TOAST.NAME_REQUIRED'));
      return;
    }
    const rows = this.attrForm.values.filter((v) => v.value.trim());

    this.attrSaving = true;
    const obs = this.attrEditingId
      ? this.attrSvc.update(this.attrEditingId, {
          name,
          values: rows.map((v, i) => ({ id: v.id, value: v.value.trim(), sortOrder: i })),
          isActive: this.attrForm.isActive,
        })
      : this.attrSvc.create({
          name,
          values: rows.map((v, i) => ({ value: v.value.trim(), sortOrder: i })),
          storeTypeId: this.selectedAttrStoreTypeId(),
        });

    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        if (this.attrEditingId) this.attrSvc.showUpdateSuccess();
        else this.attrSvc.showCreateSuccess();
        this.closeAttrDialog();
        this.loadAttributes();
      },
      error: (err) => {
        this.attrSaving = false;
        if (this.attrEditingId) this.attrSvc.showUpdateError(err);
        else this.attrSvc.showCreateError(err);
      },
      complete: () => (this.attrSaving = false),
    });
  }

  confirmDeleteAttr(attr: VariantAttributeDto) {
    this.confirmSvc.confirm({
      message: this.translate.instant('VARIANT_ATTRS.DELETE_CONFIRM', { name: attr.name }),
      header: this.translate.instant('VARIANT_ATTRS.DELETE_TITLE'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('COMMON.DELETE'),
      rejectLabel: this.translate.instant('COMMON.CANCEL'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => {
        this.attrSvc
          .delete(attr.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.attrSvc.showDeleteSuccess();
              this.loadAttributes();
            },
            error: (err) => this.attrSvc.showDeleteError(err),
          });
      },
    });
  }

  activeValuesLabel(attr: VariantAttributeDto): VariantAttributeDto['values'] {
    return attr.values.filter((v) => v.isActive);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('uz-UZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
}
